import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DrawerChip } from '../components/DrawerChip';
import { PostCard } from '../components/PostCard';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { DotRule } from '../components/DotRule';
import { fetchDrawers, fetchFeed, type FeedCursor } from '../lib/queries';
import type { Drawer, Post } from '../lib/types';
import './Archive.css';

// Group posts (already sorted newest-first) into "June 2026" buckets by
// published_at. Returns ordered [label, posts][] so render order matches the
// feed's descending order without a second sort.
function groupByMonth(posts: Post[]): Array<[string, Post[]]> {
  const order: string[] = [];
  const byKey = new Map<string, Post[]>();
  for (const p of posts) {
    const d = p.published_at ? new Date(p.published_at) : null;
    const label = d
      ? d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      : 'Undated';
    if (!byKey.has(label)) {
      byKey.set(label, []);
      order.push(label);
    }
    byKey.get(label)!.push(p);
  }
  return order.map((label) => [label, byKey.get(label)!]);
}

export default function Archive() {
  const [drawers, setDrawers] = useState<Drawer[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [cursor, setCursor] = useState<FeedCursor | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<Set<string>>(new Set());

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Drawers (filter chips) load once.
  useEffect(() => {
    const ctrl = new AbortController();
    fetchDrawers(ctrl.signal)
      // Sales Room is excluded from the archive, so drop its filter chip too.
      .then((d) => { if (!ctrl.signal.aborted) setDrawers(d.filter((x) => x.slug !== 'sales-room')); })
      .catch((err) => { if (!ctrl.signal.aborted && err?.name !== 'AbortError') console.error(err); });
    return () => ctrl.abort();
  }, []);

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  // First page — refetched whenever the (debounced) search term changes.
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const page = await fetchFeed({ signal: ctrl.signal, includeSalesRoom: false, search });
        if (ctrl.signal.aborted) return;
        setPosts(page.items);
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[archive] fetch failed', err);
        setError(err?.message ?? 'Something broke loading the archive.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [search]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !cursor) return;
    setLoadingMore(true);
    const ctrl = new AbortController();
    try {
      const page = await fetchFeed({ cursor, signal: ctrl.signal, includeSalesRoom: false, search });
      if (ctrl.signal.aborted) return;
      setPosts((prev) => [...prev, ...page.items]);
      setCursor(page.nextCursor);
      setHasMore(page.nextCursor !== null);
    } catch (err: any) {
      if (ctrl.signal.aborted || err?.name === 'AbortError') return;
      console.error('[archive] loadMore failed', err);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, hasMore, loadingMore, search]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) loadMore(); },
      { rootMargin: '600px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  function toggle(id: string) {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Drawer filter is client-side over the loaded set (mirrors the old feed).
  const filtered = useMemo(() => {
    let out = posts;
    if (active.size > 0) {
      out = out.filter((p) => (p.drawers ?? []).some((d) => active.has(d.id)));
    }
    return out;
  }, [active, posts]);

  const groups = useMemo(() => groupByMonth(filtered), [filtered]);

  return (
    <div className="archive">
      <header className="archive__header">
        <div className="container">
          {/* Back (left) · title (centred) on one line, like the lot pages. */}
          <div className="archive__topbar">
            <Link to="/" className="archive__back">← Back to the closets</Link>
            <p className="kicker">Archived</p>
          </div>
          <h1 className="archive__headline">Everything in the room.</h1>
          <DotRule className="archive__rule" />
        </div>
      </header>

      <div className="container">
        <div className="archive__controls">
          <label className="archive__search">
            <span className="archive__search-icon" aria-hidden="true">⌕</span>
            <input
              type="search"
              placeholder="Search the archive…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search the archive"
            />
          </label>

          <div className="archive__filters" aria-label="Filter by closet">
            <DrawerChip label="All" active={active.size === 0} onClick={() => setActive(new Set())} />
            {drawers.map((d) => (
              <DrawerChip
                key={d.id}
                label={d.name}
                active={active.has(d.id)}
                onClick={() => toggle(d.id)}
              />
            ))}
          </div>
        </div>

        {loading ? (
          <div className="archive__grid" aria-busy="true" aria-label="Loading the archive">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="archive__cell">
                <Skeleton variant="card" aspectRatio={16 / 9} />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState eyebrow="Error" headline="We couldn't load the archive." body={error} />
        ) : filtered.length === 0 ? (
          <EmptyState
            eyebrow={search ? 'No matches' : 'Nothing here'}
            headline={search ? `Nothing matches “${search}”.` : 'The archive is empty, for now.'}
            body={search ? 'Try a different search, or clear your filters.' : 'Published pieces will collect here over time.'}
          />
        ) : (
          <>
            {groups.map(([label, items]) => (
              <section key={label} className="archive__month">
                <h2 className="archive__month-label">{label}</h2>
                <div className="archive__grid">
                  {items.map((post) => (
                    <div key={post.id} className="archive__cell">
                      <PostCard post={post} />
                    </div>
                  ))}
                </div>
              </section>
            ))}

            {hasMore && (
              <div ref={sentinelRef} className="archive__sentinel" aria-hidden="true">
                {loadingMore ? (
                  <p className="archive__sentinel-msg">Loading more…</p>
                ) : (
                  <button type="button" className="btn btn--ghost btn--sm" onClick={loadMore}>
                    Load more
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
